// src/components/Briefs/BriefDisplay.js
import React, { useState, Fragment } from 'react';
import {
    Paper,
    Title,
    Text,
    Stack,
    Divider,
    Group,
    Badge,
    List,
    ThemeIcon,
    Blockquote,
    Radio,
    Button,
    rem,
    Tooltip,
    Box,
    Anchor,
    //Fragment,
} from '@mantine/core';
// No longer need useClipboard, so it's removed
import copy from 'copy-to-clipboard';
import { IconCheck, IconListDetails, IconBulb, IconInfoCircle, IconSettings, IconWorld, IconPhoto, IconNote, IconFileText, IconCash, IconPalette, IconClipboardText } from '@tabler/icons-react';

// A helper component to create consistent section layouts
const BriefSection = ({ title, icon, children }) => {
    return (
        <Box mt="xl">
            <Group mb="md">
                <ThemeIcon variant="light" size="lg">
                    {icon || <IconInfoCircle style={{ width: rem(22), height: rem(22) }} />}
                </ThemeIcon>
                <Title order={3}>{title}</Title>
            </Group>
            <Paper withBorder radius="md" p="md" shadow="xs">
                {children}
            </Paper>
        </Box>
    );
};

// A helper for individual fields within a section
const BriefField = ({ label, children }) => (
    <Box mt="sm">
        <Text fz="sm" tt="uppercase" c="dimmed" fw={700}>{label}</Text>
        {typeof children === 'string' ? <Text>{children}</Text> : children}
    </Box>
);

/**
 * Parses a text string and converts any style guide references into
 * clickable Anchor components that open in a new tab.
 * Handles single numbers, ranges (e.g., 6.1-6.7), and
 * comma-separated lists (e.g., 43.17, 43.19).
 * @param {string} text - The input text from the API.
 * @returns {Array<React.ReactNode>} - An array of strings and React components.
 */
const linkifyGuidelineReferences = (text) => {
    if (!text || typeof text !== 'string') {
        return text;
    }

    const regex = /(?:(Style Guide section|Style Guide|section)\s+([\d.,-]+))|(\b\d+\.\d+[\d.-]*\b(?!%))/gi;
    const matches = Array.from(text.matchAll(regex));

    if (matches.length === 0) {
        return text;
    }

    const result = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
        result.push(text.slice(lastIndex, match.index));

        const fullMatchText = match[0];
        const numberFromKeywordMatch = match[2];
        const standaloneDecimalMatch = match[3];
        let numberMatch = numberFromKeywordMatch || standaloneDecimalMatch;

        const cleanedNumberMatch = numberMatch.replace(/[.,)]+$/, '');
        const trailingChars = numberMatch.substring(cleanedNumberMatch.length);
        const numberStringToProcess = cleanedNumberMatch;

        if (numberStringToProcess.includes(',')) {
            // This logic is for comma-separated lists like "section 1.1, 1.2"
            const numbers = numberStringToProcess.split(',').map(n => n.trim());
            const firstNum = numbers[0];
            const keywordPrefix = fullMatchText.substring(0, fullMatchText.indexOf(numberMatch));

            result.push(
                <Fragment key={`${index}-fragment`}>
                    <Anchor href={`/guidelines#section-${firstNum.replace(/\./g, '-')}`} target="_blank">
                        {keywordPrefix}{firstNum}
                    </Anchor>
                    {numbers.slice(1).map((num, i) => (
                        <Fragment key={`${index}-${i}`}>
                            {', '}
                            <Anchor href={`/guidelines#section-${num.replace(/\./g, '-')}`} target="_blank">
                                {num}
                            </Anchor>
                        </Fragment>
                    ))}
                    {trailingChars}
                </Fragment>
            );
        } else {
            // This logic is for single references like "section 9.1."
            const firstNum = numberStringToProcess.split('-')[0].trim();

            // --- THE FIX IS HERE ---
            // We now use substring() to safely remove the trailing characters without
            // affecting any characters (like dots) in the middle of the string.
            const linkText = fullMatchText.substring(0, fullMatchText.length - trailingChars.length);

            result.push(
                <Fragment key={index}>
                    <Anchor href={`/guidelines#section-${firstNum.replace(/\./g, '-')}`} target="_blank">
                        {linkText}
                    </Anchor>
                    {trailingChars}
                </Fragment>
            );
        }
        lastIndex = match.index + fullMatchText.length;
    });

    result.push(text.slice(lastIndex));
    return result;
};

/**
 * Creates a raw HTML string with <a> tags for the clipboard.
 * Mirrors the logic of linkifyGuidelineReferences.
 * @param {string} text - The input text from the API.
 * @returns {string} - An HTML string.
 */

const linkifyForHTML = (text, options = {}) => { // <<< MODIFIED: Added options parameter
    // Set a default value for withLinks
    const { withLinks = true } = options;

    if (!text || typeof text !== 'string') return text;

    const regex = /(?:(Style Guide section|Style Guide|section)\s+([\d.,-]+))|(\b\d+\.\d+[\d.-]*\b(?!%))/gi;
    const baseUrl = 'https://freelancer-ideas-portal.web.app/guidelines';

    return text.replace(regex, (fullMatchText, ...args) => {
        // --- NEW: Check if we should add links at all ---
        if (!withLinks) {
            return fullMatchText; // If linking is off, just return the original text
        }

        // --- The rest of the logic is the same as before ---
        const numberFromKeywordMatch = args[1];
        const standaloneDecimalMatch = args[2];
        let numberMatch = numberFromKeywordMatch || standaloneDecimalMatch;

        if (typeof numberMatch === 'undefined') return fullMatchText;

        const cleanedNumberMatch = numberMatch.replace(/[.,)]+$/, '');
        const trailingChars = numberMatch.substring(cleanedNumberMatch.length);
        const numberStringToProcess = cleanedNumberMatch;

        if (numberStringToProcess.includes(',')) {
            const numbers = numberStringToProcess.split(',').map(n => n.trim());
            const firstNum = numbers[0];
            const keywordPrefix = fullMatchText.substring(0, fullMatchText.indexOf(numberMatch));
            const firstLinkText = `${keywordPrefix}${firstNum}`;
            const firstLink = `<a href="${baseUrl}#section-${firstNum.replace(/\./g, '-')}" target="_blank">${firstLinkText}</a>`;
            const subsequentLinks = numbers.slice(1).map(num =>
                `<a href="${baseUrl}#section-${num.replace(/\./g, '-')}" target="_blank">${num}</a>`
            ).join(', ');
            return `${firstLink}, ${subsequentLinks}${trailingChars}`;
        } else {
            const firstNum = numberStringToProcess.split('-')[0].trim();
            const linkText = fullMatchText.substring(0, fullMatchText.length - trailingChars.length);
            return `<a href="${baseUrl}#section-${firstNum.replace(/\./g, '-')}" target="_blank">${linkText}</a>${trailingChars}`;
        }
    });
};


function BriefDisplay({ brief }) {
    const { titleSuggestions, briefDetails } = brief;
    const [selectedTitle, setSelectedTitle] = useState(titleSuggestions.writerHubAlternatives[0]);

    const [richTextCopied, setRichTextCopied] = useState(false);
    const [noLinksCopied, setNoLinksCopied] = useState(false);

    const allTitlesForRadio = [
        ...titleSuggestions.writerHubAlternatives,
        titleSuggestions.submittedWorkingTitle
    ];

    const renderListFromText = (text) => {
        if (!text || typeof text !== 'string') return null;
        const items = text.split(/\n\s*[-*]\s*/).filter(item => item.trim() !== '');
        if (items.length > 1) {
            return (
                <List spacing="xs" size="sm">
                    {/* The change is HERE. We linkify each item individually. */}
                    {items.map((item, index) => (
                        <List.Item key={index}>
                            {linkifyGuidelineReferences(item.trim())}
                        </List.Item>
                    ))}
                </List>
            );
        }
        // If it's not a list, it's just a single block of text. Linkify it.
        return <Text>{linkifyGuidelineReferences(text)}</Text>;
    };

    const handleCopyRichText = () => {
        const { briefDetails } = brief;

        // AFTER
        const listToHtml = (text) => {
            if (!text || typeof text !== 'string') return '';
            const items = text.split(/\n\s*[-*]\s*/).filter(item => item.trim() !== '');
            if (items.length > 1) {
                // The change is HERE: We wrap each item in the linkify function
                return `<ul>${items.map(item => `<li>${linkifyForHTML(item.trim())}</li>`).join('')}</ul>`;
            }
            // And also apply it here for non-list text
            return `<p>${linkifyForHTML(text)}</p>`;
        };

        // Build the HTML string
        const htmlContent = `
            <h1>${selectedTitle}</h1>
            
            <p><em>This brief is built upon the top title recommendation ("${brief.titleSuggestions.writerHubAlternatives[0]}"). Please bear this in mind if you chose an alternative. </em></p>
            
            <h2>Commissioning Details</h2>
            <p><strong>Deadlines & Fees:</strong> ${briefDetails.commissioningDetails.deadlinesAndFees}</p>
            <p><strong>Writer Name & Email:</strong> ${briefDetails.commissioningDetails.writerNameAndEmail}</p>
            <p><strong>Commissioning Editor:</strong> ${briefDetails.commissioningDetails.commissioningEditor}</p>

            <h2>Summary & Scope</h2>
            <p><strong>Angle & Scope:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.angleAndScope)}</p>
            <p><strong>Slide Count & Format:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.slideCountAndFormat)}</p>
            <p><strong>Expert Input Requirement:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.expertInputRequirement)}</p>

            <h2>Slide Structure</h2>
            <p><strong>Intro Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.introSlides)}</p>
            <p><strong>Core Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.coreSlides)}</p>
            <p><strong>Subsequent Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.subsequentSlides)}</p>
            
            <h2>Resources</h2>
            ${briefDetails.resources.contentRepurposingInstructions !== 'N/A' ? `<p><strong>Content Repurposing Instructions:</strong> ${linkifyForHTML(briefDetails.resources.contentRepurposingInstructions)}</p>` : ''}
            <p><strong>Provided URLs:</strong></p>
            <ul>${briefDetails.resources.providedURLs.map(url => `<li><a href="${url}">${url}</a></li>`).join('')}</ul>
            <p><strong>Suggested Additional Resources:</strong> ${linkifyForHTML(briefDetails.resources.suggestedAdditionalResourceTypes)}</p>
            <blockquote>${linkifyForHTML(briefDetails.resources.sourceLinkingReminder)}</blockquote>
            
            <h2>Style & Tone</h2>
            <p><strong>Language:</strong> ${linkifyForHTML(briefDetails.styleAndTone.language)}</p>
            <p><strong>Key Tones:</strong></p>
            ${listToHtml(briefDetails.styleAndTone.keyTones)}
            <p><strong>Brand-Specific Style:</strong></p>
            <p>${linkifyForHTML(briefDetails.styleAndTone.brandSpecificStyle)}</p>

            <h2>Financials & Measurements</h2>
            <p><strong>Currency Style:</strong> ${linkifyForHTML(briefDetails.financialsAndMeasurements.currencyStyle)}</p>
            <p><strong>Measurement Style:</strong> ${linkifyForHTML(briefDetails.financialsAndMeasurements.measurementStyle)}</p>

            <h2>Image Guidelines</h2>
            <p><strong>Best Practices:</strong></p>
            ${listToHtml(briefDetails.imageGuidelines.generalBestPractices)}
            <p><strong>Banned Images:</strong></p>
            ${listToHtml(briefDetails.imageGuidelines.criticallyAvoidBannedImages)}
            <p><strong>Sourcing:</strong></p>
            ${listToHtml(briefDetails.imageGuidelines.imageSourcingGuidelines)}

            <h2>Final Notes & Syndication</h2>
            <p><strong>Caption Length:</strong> ${linkifyForHTML(briefDetails.otherNotesAndSyndication.captionLength)}</p>
            <p><strong>MSN AI Sensitivity:</strong> ${linkifyForHTML(briefDetails.otherNotesAndSyndication.msnAISensitivity)}</p>
            <p><strong>Writer's Notes Guidance:</strong> ${linkifyForHTML(briefDetails.writerNotesGuidance)}</p>
        `;

        copy(htmlContent, { format: 'text/html' });
        setRichTextCopied(true);
        setTimeout(() => setRichTextCopied(false), 1500);
    };

    const handleCopyNoLinks = () => {
        const { briefDetails } = brief;

        // This helper needs to pass the options along
        const listToHtml = (text, linkOptions) => {
            if (!text || typeof text !== 'string') return '';
            const items = text.split(/\n\s*[-*]\s*/).filter(item => item.trim() !== '');
            if (items.length > 1) {
                return `<ul>${items.map(item => `<li>${linkifyForHTML(item.trim(), linkOptions)}</li>`).join('')}</ul>`;
            }
            return `<p>${linkifyForHTML(text, linkOptions)}</p>`;
        };

        // Build the HTML string, passing the { withLinks: false } option
        const htmlContent = `
        <h1>${selectedTitle}</h1>
        <p><em>This brief is built upon the top title recommendation ("${brief.titleSuggestions.writerHubAlternatives[0]}"). Please bear this in mind if you chose an alternative. </em></p>
        <h2>Commissioning Details</h2>
        <p><strong>Deadlines & Fees:</strong> ${briefDetails.commissioningDetails.deadlinesAndFees}</p>
        <p><strong>Writer Name & Email:</strong> ${briefDetails.commissioningDetails.writerNameAndEmail}</p>
        <p><strong>Commissioning Editor:</strong> ${briefDetails.commissioningDetails.commissioningEditor}</p>
        <h2>Summary & Scope</h2>
        <p><strong>Angle & Scope:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.angleAndScope, { withLinks: false })}</p>
        <p><strong>Slide Count & Format:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.slideCountAndFormat, { withLinks: false })}</p>
        <p><strong>Expert Input Requirement:</strong> ${linkifyForHTML(briefDetails.summaryAndBrief.expertInputRequirement, { withLinks: false })}</p>
        <h2>Slide Structure</h2>
        <p><strong>Intro Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.introSlides, { withLinks: false })}</p>
        <p><strong>Core Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.coreSlides, { withLinks: false })}</p>
        <p><strong>Subsequent Slides:</strong> ${linkifyForHTML(briefDetails.slideStructure.subsequentSlides, { withLinks: false })}</p>
        <h2>Resources</h2>
        ${briefDetails.resources.contentRepurposingInstructions !== 'N/A' ? `<p><strong>Content Repurposing Instructions:</strong> ${linkifyForHTML(briefDetails.resources.contentRepurposingInstructions, { withLinks: false })}</p>` : ''}
        <p><strong>Provided URLs:</strong></p>
        <ul>${briefDetails.resources.providedURLs.map(url => `<li><a href="${url}">${url}</a></li>`).join('')}</ul>
        <p><strong>Suggested Additional Resources:</strong> ${linkifyForHTML(briefDetails.resources.suggestedAdditionalResourceTypes, { withLinks: false })}</p>
        <blockquote>${linkifyForHTML(briefDetails.resources.sourceLinkingReminder, { withLinks: false })}</blockquote>
        <h2>Style & Tone</h2>
        <p><strong>Language:</strong> ${linkifyForHTML(briefDetails.styleAndTone.language, { withLinks: false })}</p>
        <p><strong>Key Tones:</strong></p>
        ${listToHtml(briefDetails.styleAndTone.keyTones, { withLinks: false })}
        <p><strong>Brand-Specific Style:</strong></p>
        <p>${linkifyForHTML(briefDetails.styleAndTone.brandSpecificStyle, { withLinks: false })}</p>
        <h2>Financials & Measurements</h2>
        <p><strong>Currency Style:</strong> ${linkifyForHTML(briefDetails.financialsAndMeasurements.currencyStyle, { withLinks: false })}</p>
        <p><strong>Measurement Style:</strong> ${linkifyForHTML(briefDetails.financialsAndMeasurements.measurementStyle, { withLinks: false })}</p>
        <h2>Image Guidelines</h2>
        <p><strong>Best Practices:</strong></p>
        ${listToHtml(briefDetails.imageGuidelines.generalBestPractices, { withLinks: false })}
        <p><strong>Banned Images:</strong></p>
        ${listToHtml(briefDetails.imageGuidelines.criticallyAvoidBannedImages, { withLinks: false })}
        <p><strong>Sourcing:</strong></p>
        ${listToHtml(briefDetails.imageGuidelines.imageSourcingGuidelines, { withLinks: false })}
        <h2>Final Notes & Syndication</h2>
        <p><strong>Caption Length:</strong> ${linkifyForHTML(briefDetails.otherNotesAndSyndication.captionLength, { withLinks: false })}</p>
        <p><strong>MSN AI Sensitivity:</strong> ${linkifyForHTML(briefDetails.otherNotesAndSyndication.msnAISensitivity, { withLinks: false })}</p>
        <p><strong>Writer's Notes Guidance:</strong> ${linkifyForHTML(briefDetails.writerNotesGuidance, { withLinks: false })}</p>
    `;

        copy(htmlContent, { format: 'text/html' });
        setNoLinksCopied(true);
        setTimeout(() => setNoLinksCopied(false), 1500);
    };

    return (
        <Stack gap="lg">
            <Paper withBorder p="xl" radius="md" mt="xl">
                {/* Section for Title Suggestions */}
                <BriefSection title="Title Suggestions" icon={<IconBulb style={{ width: rem(22), height: rem(22) }} />} >
                    <Radio.Group
                        value={selectedTitle}
                        onChange={setSelectedTitle}
                        name="titleSelection"
                        label="Select your preferred working title:"
                    >
                        <Stack mt="xs" gap="xs">
                            {allTitlesForRadio.map((title, index) => (
                                <Radio key={index} value={title} label={title} />
                            ))}
                        </Stack>
                    </Radio.Group>
                </BriefSection>

                {/* Main Brief Details Section */}
                <BriefSection title="Brief Details" icon={<IconListDetails style={{ width: rem(22), height: rem(22) }} />} >
                    <Stack gap="lg">
                        <Blockquote color="blue" icon={<IconInfoCircle />} mt="xs">
                            {/* <<< 2. CHANGED: This now displays the currently selected title */}
                            Working title: "{selectedTitle}"
                        </Blockquote>
                        <Divider label="Commissioning Details" labelPosition="center" />
                        <Group grow>
                            <BriefField label="Deadlines & Fees">{briefDetails.commissioningDetails.deadlinesAndFees}</BriefField>
                            <BriefField label="Writer Name & Email">{briefDetails.commissioningDetails.writerNameAndEmail}</BriefField>
                            <BriefField label="Commissioning Editor">{briefDetails.commissioningDetails.commissioningEditor}</BriefField>
                        </Group>
                    </Stack>
                </BriefSection>

                {/* ... rest of the component is unchanged ... */}

                <BriefSection title="Summary & Scope" icon={<IconFileText style={{ width: rem(22), height: rem(22) }} />} >
                    <Stack gap="md">
                        <BriefField label="Angle & Scope">
                            <Text>{linkifyGuidelineReferences(briefDetails.summaryAndBrief.angleAndScope)}</Text>
                        </BriefField>
                        <BriefField label="Slide Count & Format">
                            <Text>{linkifyGuidelineReferences(briefDetails.summaryAndBrief.slideCountAndFormat)}</Text>
                        </BriefField>
                        <BriefField label="Expert Input Requirement">
                            {(() => {
                                const expertReqText = briefDetails.summaryAndBrief.expertInputRequirement;
                                // First, perform the logical check on the raw text
                                const isNA = expertReqText === 'N/A';
                                // Then, render the badge with the correct color and the linkified text
                                return (
                                    <Badge color={isNA ? 'gray' : 'blue'} size="lg" radius="sm">
                                        {linkifyGuidelineReferences(expertReqText)}
                                    </Badge>
                                );
                            })()}
                        </BriefField>
                    </Stack>
                </BriefSection>

                <BriefSection title="Slide Structure" icon={<IconSettings style={{ width: rem(22), height: rem(22) }} />} >
                    <Stack gap="md">
                        <BriefField label="Intro Slides">{linkifyGuidelineReferences(briefDetails.slideStructure.introSlides)}</BriefField>
                        <BriefField label="Core Slides">{linkifyGuidelineReferences(briefDetails.slideStructure.coreSlides)}</BriefField>
                        <BriefField label="Subsequent Slides">{linkifyGuidelineReferences(briefDetails.slideStructure.subsequentSlides)}</BriefField>
                    </Stack>
                </BriefSection>

                <BriefSection title="Resources" icon={<IconWorld style={{ width: rem(22), height: rem(22) }} />} >
                    <Stack gap="md">
                        {briefDetails.resources.contentRepurposingInstructions !== 'N/A' && (
                            <BriefField label="Content Repurposing Instructions">{linkifyGuidelineReferences(briefDetails.resources.contentRepurposingInstructions)}</BriefField>
                        )}
                        <BriefField label="Provided URLs">
                            {briefDetails.resources.providedURLs.length > 0 ? (
                                <List>
                                    {briefDetails.resources.providedURLs.map((url, i) => <List.Item key={i}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></List.Item>)}
                                </List>
                            ) : <Text c="dimmed">None provided.</Text>}
                        </BriefField>
                        <BriefField label="Suggested Additional Resources">{linkifyGuidelineReferences(briefDetails.resources.suggestedAdditionalResourceTypes)}</BriefField>
                        <Blockquote color="gray" radius="sm" icon={null} p="xs" mt="sm">
                            <Text fz="sm">{linkifyGuidelineReferences(briefDetails.resources.sourceLinkingReminder)}</Text>
                        </Blockquote>
                    </Stack>
                </BriefSection>

                <Group grow align="flex-start" mt="md">
                    <Stack gap="lg" style={{ flex: 1 }}>
                        <BriefSection title="Style & Tone" icon={<IconPalette style={{ width: rem(22), height: rem(22) }} />} >
                            <BriefField label="Language">{linkifyGuidelineReferences(briefDetails.styleAndTone.language)}</BriefField>
                            <BriefField label="Key Tones">{renderListFromText(briefDetails.styleAndTone.keyTones)}</BriefField>
                            <BriefField label="Brand-Specific Style">{linkifyGuidelineReferences(briefDetails.styleAndTone.brandSpecificStyle)}</BriefField>
                        </BriefSection>

                        <BriefSection title="Financials & Measurements" icon={<IconCash style={{ width: rem(22), height: rem(22) }} />} >
                            <BriefField label="Currency Style">{linkifyGuidelineReferences(briefDetails.financialsAndMeasurements.currencyStyle)}</BriefField>
                            <BriefField label="Measurement Style">{linkifyGuidelineReferences(briefDetails.financialsAndMeasurements.measurementStyle)}</BriefField>
                        </BriefSection>
                    </Stack>

                    <Stack gap="lg" style={{ flex: 1 }}>
                        <BriefSection title="Image Guidelines" icon={<IconPhoto style={{ width: rem(22), height: rem(22) }} />} >
                            <BriefField label="Best Practices">{renderListFromText(briefDetails.imageGuidelines.generalBestPractices)}</BriefField>
                            <BriefField label="Banned Images">{renderListFromText(briefDetails.imageGuidelines.criticallyAvoidBannedImages)}</BriefField>
                            <BriefField label="Sourcing">{renderListFromText(briefDetails.imageGuidelines.imageSourcingGuidelines)}</BriefField>
                        </BriefSection>
                    </Stack>
                </Group>

                <BriefSection title="Final Notes & Syndication" icon={<IconNote style={{ width: rem(22), height: rem(22) }} />} >
                    <Stack gap="md">
                        <BriefField label="Caption Length">{linkifyGuidelineReferences(briefDetails.otherNotesAndSyndication.captionLength)}</BriefField>
                        <BriefField label="MSN AI Sensitivity">{linkifyGuidelineReferences(briefDetails.otherNotesAndSyndication.msnAISensitivity)}</BriefField>
                        <BriefField label="Writer's Notes Guidance">{linkifyGuidelineReferences(briefDetails.writerNotesGuidance)}</BriefField>
                    </Stack>
                </BriefSection>

                {/* Action Buttons */}
                <Group justify="flex-end" mt="xl">
                    <Tooltip label={noLinksCopied ? 'Copied!' : 'Copy brief for external writers (no guideline links)'} withArrow>
                        <Button
                            variant="light"
                            onClick={handleCopyNoLinks}
                            leftSection={noLinksCopied ? <IconCheck size="1rem" /> : <IconClipboardText size="1rem" />}
                            color={noLinksCopied ? 'teal' : 'gray'}
                        >
                            {noLinksCopied ? 'Copied' : 'Copy (No Links)'}
                        </Button>
                    </Tooltip>

                    <Tooltip label={richTextCopied ? 'Copied!' : 'Copy brief for internal use (with guideline links)'} withArrow>
                        <Button
                            onClick={handleCopyRichText}
                            leftSection={richTextCopied ? <IconCheck size="1rem" /> : <IconClipboardText size="1rem" />}
                            color={richTextCopied ? 'teal' : 'blue'}
                        >
                            {richTextCopied ? 'Copied' : 'Copy for Docs'}
                        </Button>
                    </Tooltip>
                </Group>
            </Paper>
        </Stack>
    );
}

export default BriefDisplay;